import { FC, useEffect, useMemo, useState } from "react";
import {
  OrderOptimizerTaxiListingItem,
  OrderOptimizerTaxiListingItemRoute,
} from "../../common/types/order-optimizer-taxi-listing-item";
import MapRoute from "../../../../../../common/components/map/types/map-route";
import useOrderOptimizerRoadRoute from "../../../../../../common/services/order-optimizer/road-route/use-order-optimizer-road-route";
import MapMarker from "../../../../../../common/components/map/types/map-marker";
import orderOptimizerTaxiListingMapMarkersFactory from "../../common/order-optimizer-taxi-listing-map-markers.factory";
import orderOptimizerTranslationsHelper from "../../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerRoadRouteParams from "../../../../../../common/services/order-optimizer/road-route/order-optimizer-road-route-params";
import { SearchRoadRoutingResponseRouteGeometryCoordinate } from "../../../../../../common/utils/search-road-route/search-road-routing.response";
import orderOptimizerTaxiListingTableRowHelper from "./order-optimizer-taxi-listing-table-row.helper";
import orderOptimizerTaxiListingRouteFactory from "../../common/order-optimizer-taxi-listing-route.factory";
import orderOptimizerHelper from "../../../../common/order-optimizer.helper";
import MapComponent from "../../../../../../common/components/map/map.component";
import ButtonComponent from "../../../../../../common/components/button/button.component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faWrench } from "@fortawesome/free-solid-svg-icons";
import LinkButtonComponent from "../../../../../../common/components/button/link/link-button.component";
import orderOptimizerRoutesHelper from "../../../../common/routes/order-optimizer-routes.helper";
import orderOptimizerTaxiDetailsWaypointFactory from "../../common/order-optimizer-taxi-listing-waypoint.factory";
import classNames from "classnames";
import ClockIconComponent from "../../../../common/icons/clock-icon-component";

type OrderOptimizerTaxiListingTableRowProps = {
  listingItem: OrderOptimizerTaxiListingItem;
  selectedHumanId: number | undefined;
  onOptimizationRequestClick: () => void;
};

const OrderOptimizerTaxiListingTableRowComponent: FC<
  OrderOptimizerTaxiListingTableRowProps
> = (props) => {
  const [waypoints, setWaypoints] = useState<
    OrderOptimizerTaxiListingItemRoute[]
  >([]);

  const [mapRoutes, setMapRoutes] = useState<MapRoute[]>([]);

  const roadRoute = useOrderOptimizerRoadRoute();

  const mapMarkers: MapMarker[] = useMemo(() => {
    const mapMarkers =
      orderOptimizerTaxiListingMapMarkersFactory.createMapMarkers(waypoints);

    return mapMarkers;
  }, [waypoints]);

  const translations =
    orderOptimizerTranslationsHelper.getTaxiListingTranslations().table;

  useEffect(() => {
    if (!props.listingItem) {
      return;
    }

    const preparedWaypoints =
      orderOptimizerTaxiDetailsWaypointFactory.createWaypoints(
        props.listingItem,
      );

    setWaypoints(preparedWaypoints.flat());

    const loadParams: OrderOptimizerRoadRouteParams[] = [];

    preparedWaypoints.forEach((preparedWaypointsRoute) => {
      if (preparedWaypointsRoute.length > 1) {
        const request =
          orderOptimizerTaxiDetailsWaypointFactory.createSearchRoutingRequest(
            preparedWaypointsRoute,
          );
        loadParams.push(request);
      }
    });

    roadRoute.load(loadParams);
  }, [props.listingItem]);

  useEffect(() => {
    if (!roadRoute.data) {
      return;
    }

    const routes = roadRoute.data.map((route, index) => {
      const formattedCoordinates: SearchRoadRoutingResponseRouteGeometryCoordinate[] =
        route.coordinates;

      return orderOptimizerTaxiListingRouteFactory.createMapRoute(
        formattedCoordinates,
        orderOptimizerHelper.getOrderColor(index),
      );
    });

    setMapRoutes(routes);
  }, [roadRoute.data]);

  const scoreLabel = orderOptimizerTaxiListingTableRowHelper.getScoreLabel(
    props.listingItem.score,
  );

  const idLabel = orderOptimizerTaxiListingTableRowHelper.getIdLabel(
    Number(props.listingItem.id),
  );

  const statusClassNames = classNames(
    "order_optimizer_taxi_table_row_status",
    orderOptimizerTaxiListingTableRowHelper.getClassNameByStatus(
      props.listingItem.status,
    ),
  );

  return (
    <div className="order_optimizer_taxi_table_row">
      <div className={statusClassNames} />
      <div className="order_optimizer_taxi_table_row__left">
        <MapComponent markers={mapMarkers} autoFit routes={mapRoutes} />
      </div>
      <div className="order_optimizer_taxi_table_row__right">
        <div className="order_optimizer_taxi_table_row__right__upper_section">
          <div className="order_optimizer_taxi_table_row__right__upper_section__left">
            <h1 className="order_optimizer_taxi_table_row__right__upper_section__text">
              {scoreLabel}
            </h1>
            <h1 className="order_optimizer_taxi_table_row__right__upper_section__text">
              {idLabel}
            </h1>
          </div>
          <div className="order_optimizer_taxi_table_row__right__upper_section__right">
            {props.listingItem.reviewedBy === null ? (
              <ButtonComponent
                type="primary"
                title={translations.optimizationButtonTitle}
                classNames={{
                  root: "order_optimizer_taxi_table_row__right__upper_section__right__button",
                }}
                onClick={props.onOptimizationRequestClick}
              >
                <div className="order_optimizer_taxi_table_row__text">
                  {translations.optimizationButtonLabel}
                  <FontAwesomeIcon
                    icon={faWrench}
                    className="order_optimizer_taxi_table_row__right__upper_section__right__button__icon"
                  />
                </div>
              </ButtonComponent>
            ) : (
              <>
                <LinkButtonComponent
                  to={orderOptimizerRoutesHelper.getDetailsRoute({
                    id: props.listingItem.id,
                  })}
                  type="primary"
                  title={translations.previewOptimizationButtonTitle}
                  classNames={{
                    root: "order_optimizer_taxi_table_row__right__upper_section__right__button_preview",
                  }}
                  openInNewTab
                >
                  <div className="order_optimizer_taxi_table_row__text">
                    {translations.previewOptimizationButtonLabel}
                    <FontAwesomeIcon
                      icon={faEye}
                      className="order_optimizer_taxi_table_row__right__upper_section__right__button_preview__icon"
                    />
                  </div>
                </LinkButtonComponent>
                <div
                  className="order_optimizer_taxi_table_row__right__upper_section__right__badge"
                  title={`${
                    props.listingItem.reviewedBy
                  }, ${orderOptimizerTaxiListingTableRowHelper.getDateLabel(
                    props.listingItem.reviewedAt,
                  )}`}
                >
                  {`${
                    props.listingItem.reviewedBy
                  }, ${orderOptimizerTaxiListingTableRowHelper.getDateLabel(
                    props.listingItem.reviewedAt,
                  )}`}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="order_optimizer_taxi_table_row__right__lower_section">
          <div className="order_optimizer_taxi_table_row__right__lower_section__left">
            <div className="order_optimizer_taxi_table_row__right__lower_section__left__item">
              <h3 className="order_optimizer_taxi_table_row__right__lower_section__left__item__text">
                {translations.orderNumbersLabel}
              </h3>
              {props.listingItem.orders.map((order) => (
                <div
                  key={order.humanId}
                  className={
                    props.selectedHumanId === order.humanId
                      ? "order_optimizer_taxi_table_row__right__lower_section__left__item__highlighted"
                      : "order_optimizer_taxi_table_row__right__lower_section"
                  }
                >
                  {order.humanId}
                  {order.haltingTimeBefore < 0 ? (
                    <div
                      title={orderOptimizerTaxiListingTableRowHelper.getDelayTitle(
                        -order.haltingTimeBefore,
                      )}
                      className="order_optimizer_taxi_table_row__right__lower_section__left__item__delay"
                    >
                      <ClockIconComponent className="order_optimizer_taxi_table_row__right__lower_section__left__item__delay__icon" />
                      {orderOptimizerTaxiListingTableRowHelper.getDelayLabel(
                        -order.haltingTimeBefore,
                      )}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="order_optimizer_taxi_table_row__right__lower_section__left__item">
              <h3 className="order_optimizer_taxi_table_row__right__lower_section__left__item__text">
                {translations.startDateLabel}
              </h3>
              {props.listingItem.orders.map((order) => (
                <div
                  key={order.humanId}
                  className="order_optimizer_taxi_table_row__right__lower_section"
                >
                  {orderOptimizerTaxiListingTableRowHelper.getDateLabel(
                    order.startTime,
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="order_optimizer_taxi_table_row__right__lower_section__right">
            <h3 className="order_optimizer_taxi_table_row__right__lower_section__right__text">
              {translations.cargoCompanyLabel}
            </h3>
            {props.listingItem.orders.map((order) => (
              <div
                key={order.humanId}
                className="order_optimizer_taxi_table_row__right__lower_section"
              >
                {order.cargoCompany}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderOptimizerTaxiListingTableRowComponent;
